<template>
  <div
    v-if="isAuthenticated && !isOnChatPage"
    class="floating-chat-wrapper"
  >
    <!-- FAB Button -->
    <button
      class="floating-chat-fab"
      :class="{ 'fab-active': isOpen }"
      @click="toggleChat"
    >
      <feather-icon
        :icon="isOpen ? 'XIcon' : 'MessageSquareIcon'"
        size="22"
      />
    </button>

    <!-- Chat Window -->
    <transition name="floating-chat-slide">
      <div
        v-if="isOpen"
        class="floating-chat-window"
        :class="{ 'fc-expanded': isExpanded }"
      >
        <!-- Header -->
        <div class="fc-header">
          <div class="d-flex align-items-center">
            <div class="fc-avatar">
              <feather-icon
                icon="ZapIcon"
                size="16"
              />
            </div>
            <div class="ml-75">
              <div class="d-flex align-items-center">
                <h6 class="mb-0 text-white">
                  Asistente Dutton
                </h6>
              </div>
              <small class="text-white-50">IA de consultas</small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <button
              v-if="messages.length > 0"
              class="fc-header-btn mr-50"
              title="Limpiar chat"
              @click="clearChat"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
              />
            </button>
            <button
              class="fc-header-btn mr-50"
              :title="isExpanded ? 'Reducir' : 'Expandir'"
              @click="isExpanded = !isExpanded"
            >
              <feather-icon
                :icon="isExpanded ? 'MinimizeIcon' : 'MaximizeIcon'"
                size="14"
              />
            </button>
            <button
              class="fc-header-btn"
              title="Cerrar"
              @click="toggleChat"
            >
              <feather-icon
                icon="MinusIcon"
                size="14"
              />
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div
          ref="chatBody"
          class="fc-body"
          @click="handleEntityClick"
        >
          <!-- Welcome -->
          <div
            v-if="messages.length === 0"
            class="fc-welcome"
          >
            <div class="fc-welcome-icon">
              <feather-icon
                icon="ZapIcon"
                size="28"
              />
            </div>
            <p class="mb-50">
              <strong>{{ welcomeData ? welcomeData.greeting : 'Hola!' }}</strong>
              Soy el Asistente Dutton.
            </p>
            <div
              v-if="welcomeData && (welcomeData.pending_orders > 0 || welcomeData.pending_quotes > 0)"
              class="fc-welcome-stats mb-75"
            >
              <small class="d-block text-muted">
                <span v-if="welcomeData.pending_orders > 0">
                  <feather-icon
                    icon="BoxIcon"
                    size="12"
                    class="mr-25"
                  />
                  {{ welcomeData.pending_orders }} pedidos pendientes
                </span>
                <span v-if="welcomeData.pending_orders > 0 && welcomeData.pending_quotes > 0"> · </span>
                <span v-if="welcomeData.pending_quotes > 0">
                  <feather-icon
                    icon="FileTextIcon"
                    size="12"
                    class="mr-25"
                  />
                  {{ welcomeData.pending_quotes }} cotizaciones pendientes
                </span>
              </small>
            </div>
            <small
              v-else
              class="text-muted"
            >Preguntame sobre pedidos, cotizaciones, productos o clientes.</small>

            <div class="fc-quick-actions mt-1">
              <button
                v-for="(q, i) in quickQuestions"
                :key="i"
                class="fc-quick-btn"
                :disabled="loading"
                @click="askQuestion(q)"
              >
                {{ q }}
              </button>
            </div>
          </div>

          <!-- Messages -->
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="fc-message"
            :class="msg.role === 'user' ? 'fc-message-user' : 'fc-message-assistant'"
          >
            <div
              class="fc-msg-avatar"
              :class="msg.role === 'user' ? 'fc-avatar-user' : 'fc-avatar-ai'"
            >
              <feather-icon
                :icon="msg.role === 'user' ? 'UserIcon' : 'ZapIcon'"
                size="12"
              />
            </div>
            <div
              v-if="msg.role === 'assistant'"
              class="fc-bubble-wrapper"
              @mouseenter="hoveredMsg = index"
              @mouseleave="hoveredMsg = null"
            >
              <div class="fc-bubble fc-bubble-assistant">
                <button
                  v-if="hoveredMsg === index"
                  class="fc-copy-btn"
                  title="Copiar mensaje"
                  @click="copyMessage(msg.content)"
                >
                  <feather-icon
                    icon="CopyIcon"
                    size="15"
                  />
                </button>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="formatMessage(msg.content, streaming && index === messages.length - 1)" />
              </div>
              <div
                v-if="hoveredMsg === index || feedbackGiven[index]"
                class="fc-feedback-btns"
              >
                <button
                  class="fc-feedback-btn"
                  :class="{ 'fc-feedback-active': feedbackGiven[index] === 'positive' }"
                  title="Útil"
                  @click="submitFeedback(index, 'positive')"
                >
                  <feather-icon
                    icon="ThumbsUpIcon"
                    size="12"
                  />
                </button>
                <button
                  class="fc-feedback-btn"
                  :class="{ 'fc-feedback-active-neg': feedbackGiven[index] === 'negative' }"
                  title="No útil"
                  @click="submitFeedback(index, 'negative')"
                >
                  <feather-icon
                    icon="ThumbsDownIcon"
                    size="12"
                  />
                </button>
              </div>
            </div>
            <div
              v-else
              class="fc-bubble fc-bubble-user"
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="formatMessage(msg.content)" />
            </div>
          </div>

          <!-- Tools used indicator -->
          <div
            v-if="toolsUsed.length > 0 && messages.length > 0 && messages[messages.length - 1].role === 'assistant'"
            class="fc-tools-indicator"
          >
            <feather-icon
              icon="SearchIcon"
              size="11"
            />
            <span>Consultó: {{ toolsUsedLabels.join(', ') }}</span>
          </div>

          <!-- Typing indicator -->
          <div
            v-if="loading && !streaming"
            class="fc-message fc-message-assistant"
          >
            <div class="fc-msg-avatar fc-avatar-ai">
              <feather-icon
                icon="ZapIcon"
                size="12"
              />
            </div>
            <div class="fc-bubble fc-bubble-assistant">
              <div class="fc-typing">
                <span /><span /><span />
              </div>
              <div class="fc-loading-text">
                {{ loadingMessage }}
              </div>
            </div>
          </div>
        </div>

        <!-- Post-response suggestions (only when there are messages) -->
        <div
          v-if="postSuggestions.length > 0 && messages.length > 0 && !loading"
          class="fc-suggestions"
        >
          <button
            v-for="(s, i) in postSuggestions"
            :key="'post-' + i"
            class="fc-suggestion-chip"
            @click="askQuestion(s)"
          >
            {{ s }}
          </button>
        </div>
        <!-- Contextual suggestions (only when there are messages and no post-suggestions) -->
        <div
          v-else-if="contextualSuggestions.length > 0 && messages.length > 0 && !loading"
          class="fc-suggestions"
        >
          <button
            v-for="(s, i) in contextualSuggestions"
            :key="'ctx-' + i"
            class="fc-suggestion-chip"
            @click="askQuestion(s)"
          >
            {{ s }}
          </button>
        </div>

        <!-- Input -->
        <div class="fc-input-area">
          <div
            v-if="currentContext"
            class="fc-context-chip"
          >
            <feather-icon
              icon="LinkIcon"
              size="11"
            />
            <span>{{ currentContext.label }}</span>
            <button
              class="fc-context-close"
              @click="clearContext"
            >
              <feather-icon
                icon="XIcon"
                size="10"
              />
            </button>
          </div>
          <div
            class="fc-input-wrapper"
            :class="{ 'fc-input-focused': inputFocused }"
          >
            <input
              ref="chatInput"
              v-model="userMessage"
              type="text"
              class="fc-input"
              placeholder="Escribe tu pregunta..."
              :disabled="loading"
              @keydown.enter.prevent="sendMessage"
              @focus="inputFocused = true"
              @blur="inputFocused = false"
            >
            <button
              class="fc-send-btn"
              :class="{ 'fc-send-active': userMessage.trim() && !loading }"
              :disabled="loading || !userMessage.trim()"
              @click="sendMessage"
            >
              <feather-icon
                icon="ArrowUpIcon"
                size="14"
              />
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mixinChat } from '@/utils/mixins/chat'

export default {
  name: 'FloatingChat',
  mixins: [mixinChat],
  data() {
    return {
      isOpen: false,
      isExpanded: false,
      hoveredMsg: null,
      quickQuestions: [
        'Cuantos pedidos pendientes tengo?',
        'Mis ultimas 5 cotizaciones',
        'Pedidos en despacho',
      ],
      postSuggestions: [],
    }
  },
  computed: {
    isAuthenticated() {
      // eslint-disable-next-line no-unused-expressions
      this.$route
      return !!localStorage.getItem('DuttonAuthToken')
    },
    isOnChatPage() {
      return this.$route && this.$route.name === 'chat-assistant'
    },
    contextualSuggestions() {
      const map = {
        'contacts-customers': ['¿Cuántos clientes activos hay?', 'Buscar cliente por nombre'],
        'sales-customer-orders': ['¿Cuántos pedidos pendientes tengo?', 'Pedidos en despacho hoy'],
        'sales-customer-quotes': ['Mis últimas cotizaciones', 'Cotizaciones aprobadas este mes'],
        'catalogs-products': ['Buscar producto por SKU', 'Verificar stock de un producto'],
        home: ['Resumen de pedidos del día', '¿Cuántas cotizaciones pendientes?'],
      }
      return map[this.$route && this.$route.name] || []
    },
    routeContext() {
      if (!this.$route) return null
      const { name, params } = this.$route
      if (!params || !params.id) return null
      const map = {
        'sales-customer-orders': { type: 'order', label: 'Pedido' },
        'sales-customer-quotes': { type: 'quote', label: 'Cotización' },
      }
      const info = map[name]
      if (!info) return null
      return { type: info.type, id: params.id, label: `${info.label} #${params.id}` }
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.$nextTick(() => {
          this.scrollToBottom()
          if (this.$refs.chatInput) {
            this.$refs.chatInput.focus()
          }
        })
      }
    },
    $route(to) {
      if (to.name === 'login') {
        this.isOpen = false
      }
    },
    routeContext(val) {
      if (val) {
        this.setContext(val.type, val.id, val.label)
      } else {
        this.clearContext()
      }
    },
  },
  mounted() {
    const keepOpen = localStorage.getItem('dutton_chat_keep_open')
    if (keepOpen) {
      localStorage.removeItem('dutton_chat_keep_open')
      this.isOpen = true
    }
  },
  methods: {
    toggleChat() {
      if (this.isOnChatPage) {
        return
      }
      this.isOpen = !this.isOpen
    },
  },
}
</script>

<style scoped>
/* ---- FAB Button ---- */
.floating-chat-wrapper {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

.floating-chat-fab {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #12406f 0%, #1a5a9e 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(18, 64, 111, 0.4);
  transition: all 0.3s ease;
}

.floating-chat-fab:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 20px rgba(18, 64, 111, 0.5);
}

.floating-chat-fab.fab-active {
  background: linear-gradient(135deg, #ea5455 0%, #f08182 100%);
  box-shadow: 0 4px 14px rgba(234, 84, 85, 0.4), 0 0 0 3px rgba(255, 218, 0, 0.3);
}

/* ---- Chat Window ---- */
.floating-chat-window {
  position: absolute;
  bottom: 64px;
  right: 0;
  width: 380px;
  height: 520px;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ---- Transition ---- */
.floating-chat-slide-enter-active,
.floating-chat-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.floating-chat-slide-enter,
.floating-chat-slide-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.95);
}

/* ---- Header ---- */
.fc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #12406f 0%, #1a5a9e 100%);
  color: #fff;
  flex-shrink: 0;
}

.fc-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.fc-header-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.fc-header-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* ---- Body ---- */
.fc-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  min-height: 0;
}

.fc-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem 0.5rem;
  height: 100%;
}

.fc-welcome-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #12406f 0%, #1a5a9e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-bottom: 0.75rem;
}

.fc-welcome-stats {
  text-align: center;
}

.fc-quick-actions {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
}

.fc-quick-btn {
  padding: 0.4rem 0.65rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  background: #fff;
  color: #5e5873;
  font-size: 0.83rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}

.fc-quick-btn:hover:not(:disabled) {
  background: #f3f2f7;
  border-color: #12406f;
  color: #12406f;
}

.fc-quick-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ---- Messages ---- */
.fc-message {
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 0.4rem;
}

.fc-message-user {
  flex-direction: row-reverse;
}

.fc-msg-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fc-avatar-user {
  background: #12406f;
  color: #fff;
}

.fc-avatar-ai {
  background: #dce8f5;
  color: #12406f;
}

.fc-bubble-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 75%;
}

.fc-bubble {
  max-width: 100%;
  padding: 0.6rem 0.8rem;
  border-radius: 0.85rem;
  word-wrap: break-word;
  overflow-x: auto;
  line-height: 1.45;
  font-size: 0.9rem;
}

.fc-message-user .fc-bubble {
  max-width: 75%;
}

.fc-bubble-user {
  background-color: #12406f;
  color: #fff;
  border-top-right-radius: 0.2rem;
}

.fc-bubble-user ::v-deep p:last-child,
.fc-bubble-assistant ::v-deep p:last-child {
  margin-bottom: 0;
}

.fc-bubble ::v-deep table {
  border-collapse: collapse;
  font-size: 0.82rem;
  margin: 0.4rem 0;
  border: 1px solid #12406f;
  border-radius: 4px;
}

.fc-bubble ::v-deep th,
.fc-bubble ::v-deep td {
  border: 1px solid rgba(18, 64, 111, 0.3);
  padding: 0.3rem 0.5rem;
  white-space: nowrap;
}

.fc-bubble ::v-deep th {
  background: #dce8f5;
  font-weight: 600;
  color: #12406f;
}

.fc-bubble ::v-deep td {
  background: #fff;
}

.fc-bubble ::v-deep tr:hover td {
  background: #f0f5fc;
}

.fc-bubble ::v-deep .chat-entity-link {
  color: #12406f;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-style: dotted;
  cursor: pointer;
}

.fc-bubble ::v-deep .chat-entity-link:hover {
  text-decoration-style: solid;
}

.fc-bubble-assistant {
  background-color: #f3f2f7;
  color: #333;
  border-top-left-radius: 0.2rem;
}

/* ---- Copy button ---- */
.fc-bubble-assistant {
  position: relative;
}

.fc-copy-btn {
  position: absolute;
  top: 5px;
  right: 6px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  color: #6e6b7b;
  padding: 4px 5px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s, background 0.2s;
  line-height: 1;
}

.fc-copy-btn:hover {
  color: #12406f;
  background: #fff;
}

/* ---- Feedback buttons ---- */
.fc-feedback-btns {
  display: flex;
  gap: 2px;
  margin-top: 3px;
}

.fc-feedback-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #b4b2be;
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}

.fc-feedback-btn:hover {
  color: #12406f;
}

.fc-feedback-active {
  color: #28c76f !important;
}

.fc-feedback-active-neg {
  color: #ea5455 !important;
}

/* ---- Tools indicator ---- */
.fc-tools-indicator {
  font-size: 12px;
  color: #b4b2be;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: -0.35rem;
  margin-bottom: 0.5rem;
  padding-left: 34px;
}

/* ---- Typing ---- */
.fc-typing {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 0.15rem 0;
}

.fc-typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #a8a5b5;
  animation: fc-typing 1.2s infinite ease-in-out;
}

.fc-typing span:nth-child(2) {
  animation-delay: 0.2s;
}

.fc-typing span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes fc-typing {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-3px); opacity: 1; }
}

.fc-loading-text {
  font-size: 0.78rem;
  color: #a8a5b5;
  margin-top: 0.3rem;
}

/* ---- Input ---- */
.fc-input-area {
  flex-shrink: 0;
  padding: 0.6rem 0.75rem;
  border-top: 1px solid #ebe9f1;
}

.fc-input-wrapper {
  display: flex;
  align-items: center;
  background: #f8f8f8;
  border: 2px solid #ebe9f1;
  border-radius: 1.25rem;
  padding: 0.2rem 0.2rem 0.2rem 0.75rem;
  transition: all 0.2s ease;
}

.fc-input-wrapper.fc-input-focused {
  border-color: #12406f;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(18, 64, 111, 0.08);
}

.fc-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: #5e5873;
  padding: 0.35rem 0;
  min-width: 0;
}

.fc-input::placeholder {
  color: #b4b2be;
}

.fc-input:disabled {
  opacity: 0.6;
}

.fc-send-btn {
  width: 30px;
  height: 30px;
  min-width: 30px;
  padding: 0;
  border-radius: 50%;
  border: none;
  background: #d8d6de;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.fc-send-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.fc-send-active {
  background: #12406f;
  color: #fff;
}

.fc-send-active:hover {
  background: #0d2d4f;
}

/* ---- Suggestions ---- */
.fc-suggestions {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-top: 1px solid #ebe9f1;
}

.fc-suggestion-chip {
  font-size: 0.72rem;
  padding: 0.25rem 0.6rem;
  border: 1px solid #dce8f5;
  border-radius: 1rem;
  background: #f5f9ff;
  color: #12406f;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.fc-suggestion-chip:hover {
  background: #12406f;
  color: #fff;
  border-color: #12406f;
}

/* ---- Context chip ---- */
.fc-context-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  color: #12406f;
  background: #dce8f5;
  padding: 3px 6px 3px 8px;
  border-radius: 1rem;
  margin-bottom: 0.35rem;
}

.fc-context-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #12406f;
  padding: 0;
  display: flex;
  align-items: center;
  opacity: 0.6;
}

.fc-context-close:hover {
  opacity: 1;
}

/* ---- Expanded mode ---- */
.floating-chat-window.fc-expanded {
  width: 560px;
  height: 75vh;
}

/* ---- Responsive ---- */
@media (max-width: 480px) {
  .floating-chat-window {
    width: calc(100vw - 2rem);
    height: calc(100vh - 8rem);
    right: -0.5rem;
  }
}
</style>
